/**
 * Calculate delivery dates based on location and delivery type
 * @param {string} deliveryType - 'express' or 'normal'
 * @param {string} location - User location (city/state) or null for default
 * @param {number} expressDays - Express delivery days (default: 1 for tomorrow)
 * @param {number} normalDays - Normal delivery days (default: 4)
 * @returns {Object} - Delivery date information
 */
export const calculateDeliveryDate = (deliveryType = 'express', location = null, expressDays = 1, normalDays = 4) => {
  const today = new Date();
  const deliveryDate = new Date(today);
  
  if (deliveryType === 'express') {
    // Express delivery: tomorrow (or expressDays from today)
    deliveryDate.setDate(today.getDate() + expressDays);
  } else {
    // Normal delivery: current date + normalDays (default 4 days)
    deliveryDate.setDate(today.getDate() + normalDays);
  }
  
  // Format date
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const dayName = dayNames[deliveryDate.getDay()];
  const month = monthNames[deliveryDate.getMonth()];
  const day = deliveryDate.getDate();
  const year = deliveryDate.getFullYear();
  
  // Check if it's tomorrow
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const isTomorrow = deliveryDate.toDateString() === tomorrow.toDateString();
  
  // Format delivery text
  let deliveryText = '';
  if (deliveryType === 'express') {
    if (isTomorrow) {
      deliveryText = 'express Tomorrow';
    } else {
      deliveryText = `express ${dayName}, ${month} ${day}`;
    }
  } else {
    deliveryText = `Get by ${dayName}, ${month} ${day}, ${year}`;
  }
  
  // Add location info if available
  if (location) {
    deliveryText += ` (${location})`;
  }
  
  return {
    date: deliveryDate,
    formattedDate: `${dayName}, ${month} ${day}, ${year}`,
    deliveryText: deliveryText,
    isTomorrow: isTomorrow,
    daysFromNow: deliveryType === 'express' ? expressDays : normalDays
  };
};

/**
 * Get delivery options for a product
 * @param {Object} product - Product object
 * @param {string} location - User location
 * @returns {Object} - Delivery options with dates
 */
export const getDeliveryOptions = (product, location = null) => {
  // Get delivery days from product or use defaults
  const expressDays = product.express_delivery_days || 1;
  const normalDays = product.normal_delivery_days || 4;
  
  const express = calculateDeliveryDate('express', location, expressDays, normalDays);
  const normal = calculateDeliveryDate('normal', location, expressDays, normalDays);
  
  return {
    express: express,
    normal: normal,
    hasExpress: true,
    hasNormal: true
  };
};

